import styles from './index.module.css'

import cn from 'clsx'
import React, { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'

import {
  setPlaylistCreatorWidth,
  toggleShortlists,
} from '@disco/state-management/actions/AppActions'
import { getPlaylistCreatorWidth } from '@disco/utils/elementSizes'

import { Flex, IconButton, Tooltip } from 'components/ds'
import Icon from 'components/ui/icon'

import { useDispatch, useSelector } from 'hooks/use-redux'

import PlaylistCreatorDragOpenWrapper from './dnd-playlist-container'

type PlaylistCreatorProps = {
  visible: boolean
  onClose: () => void
  children: React.ReactNode
  isPhone?: boolean
  isBannerVisible?: boolean
}

type PlaylistToggleButtonProps = {
  tooltipVisible: boolean
  tooltipContent?: string
  children: React.ReactNode
  openPlaylistCreator: () => void
}

const PlaylistToggleButton = ({
  tooltipVisible,
  tooltipContent,
  children,
  openPlaylistCreator,
}: PlaylistToggleButtonProps) => {
  if (!tooltipVisible) return children
  return (
    <Tooltip content={tooltipContent} side="left">
      <PlaylistCreatorDragOpenWrapper onDrag={openPlaylistCreator}>
        {children}
      </PlaylistCreatorDragOpenWrapper>
    </Tooltip>
  )
}

const PlaylistCreator = ({
  visible,
  onClose,
  children,
  isPhone,
  isBannerVisible = false,
}: PlaylistCreatorProps) => {
  const [maxWidth, setMaxWidth] = useState<string>('0px')
  const dispatch = useDispatch()
  const location = useLocation()
  const pathname = location.pathname
  const isSearchPage = pathname.includes('/search')
  const isDiscoverPage = pathname.startsWith('/discover')
  const isFullHeightPage = isSearchPage || isDiscoverPage
  const currentLocation = isSearchPage
    ? 'Search'
    : isDiscoverPage
    ? 'Discover'
    : 'Explorer'
  const { windowWidth, sidebarOpen, launchedOnMobileDevice } = useSelector(
    state => state.app,
  )

  useEffect(() => {
    // adjust max width based on window size and sidebar state
    const width = getPlaylistCreatorWidth(
      windowWidth,
      sidebarOpen,
      launchedOnMobileDevice,
      visible,
    )
    setMaxWidth(`${width}px`)
    // store width in redux for use in other components
    dispatch(setPlaylistCreatorWidth(width))
  }, [visible, sidebarOpen, windowWidth])

  return (
    <Flex
      aria-label="Playlist Creator"
      aria-expanded={visible}
      direction="column"
      className={cn(styles.sidebarContainer, {
        [styles.sidebarContainer_Closed]: !visible,
        [styles.isPhone]: isPhone,
        [styles.withBanner]: isBannerVisible,
        [styles.searchPage]: isFullHeightPage,
      })}
      maxWidth={maxWidth}
    >
      <Flex
        direction="column"
        className={styles.shortlistContent}
        data-testid="shortlist-content"
      >
        {children}
      </Flex>
      <PlaylistToggleButton
        tooltipContent={!visible ? 'Open Playlist Creator' : undefined}
        tooltipVisible={!visible}
        openPlaylistCreator={() =>
          dispatch(toggleShortlists(undefined, currentLocation, 'Drag Track'))
        }
      >
        <IconButton
          size="3"
          className={styles.toggleSidebar}
          variant="solid"
          onClick={onClose}
        >
          <Icon
            className={cn(styles.toggleArrow, {
              [styles.toggleClosed]: !visible,
              [styles.toggleOpen]: visible,
            })}
            type="arrow-right"
            width="30px"
            height="30px"
            color="var(--indigo-1)"
          />
        </IconButton>
      </PlaylistToggleButton>
    </Flex>
  )
}
export default PlaylistCreator
